import { useTranslation } from '../../../lib/i18n';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useOpenSideChat } from '../../../lib/use-open-side-chat';
import { useTaskSidePanel } from '../task-side-panel/TaskSidePanelContext';
import type { SkillSummary } from '../../../lib/my-agents';

// Everything the "Agent skills" modal DOES, independent of how it is drawn — so the desktop
// dialog and the mobile full-screen sheet are two presentations of ONE behaviour rather than
// two implementations of it. Each surface only decides its own layout.
//
// There is no skill CRUD API: skills are taught to and forgotten by the Maestro in conversation,
// so both actions log BI, close the modal, and hand the Maestro an instruction in the side chat
// rather than calling an endpoint — the same handoff the standalone skills page used.
const PAGE = 'your_agents';
const MODAL = 'agent_skills';

export function useSkillsModal(skills: SkillSummary[]) {
  const { t } = useTranslation();
  const { logModalActionBi } = useGenericBi();
  const openSideChat = useOpenSideChat();
  const { closeModal } = useTaskSidePanel();

  const askMaestro = (action: string, message: string) => {
    logModalActionBi({ page_name: PAGE, modal_name: MODAL, action });
    closeModal();
    openSideChat({ kind: 'new', message });
  };

  // The active-skill count doubles as the desktop title; the mobile sheet shows it as a subtitle
  // under its own 32px heading, so both the count line and the plain heading are exposed.
  const activeCount =
    skills.length === 1
      ? t('agentSkills.skillsActiveOne', { count: skills.length })
      : t('agentSkills.skillsActive', { count: skills.length });
  const title = skills.length > 0 ? activeCount : t('agentSkills.title');

  return {
    title,
    activeCount,
    addSkill: () => askMaestro('add_skill', t('agentSkills.addSeed')),
    removeSkill: (skill: SkillSummary) => askMaestro('remove_skill', t('agentSkills.removeSeed', { skill: skill.name })),
    // Bare close for the shell's onClose (Escape / scrim) and the desktop "Close" button — no BI,
    // matching the dialog's existing behaviour.
    dismiss: closeModal,
    // The mobile "‹ Back" button is a deliberate nav click, so it reports one.
    back: () => {
      logModalActionBi({ page_name: PAGE, modal_name: MODAL, action: 'back' });
      closeModal();
    },
  };
}
